"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { Button, GlassCard, Input } from "@/components/ui";
import { Wordmark } from "@/components/shell";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { login, ApiError } from "@/lib/api";
import { saveMe } from "@/lib/auth-store";
import { SUPPORT_EMAIL } from "@/lib/legal";
import { useTurnstile } from "@/lib/use-turnstile";

export function LoginForm() {
  const t = useTranslations("login");
  const tCommon = useTranslations("common");
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [banned, setBanned] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const turnstile = useTurnstile();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBanned(false);
    setRejected(false);
    setSubmitting(true);
    try {
      const { user } = await login(email.trim(), password, turnstile.token ?? undefined);
      saveMe({
        username: user.username,
        profileType: user.profileType,
        role: user.role,
        status: user.status,
      });
      // Pending members haven't cleared manual approval yet → waiting room.
      router.push(user.status === "pending" ? "/pending" : "/deck");
    } catch (err) {
      if (err instanceof ApiError && err.code === "TURNSTILE_FAILED") {
        turnstile.reset();
        setError(t("errors.turnstileFailed"));
      } else {
        if (err instanceof ApiError && err.code === "BANNED") setBanned(true);
        if (err instanceof ApiError && err.code === "REJECTED") setRejected(true);
        const key = errorKeyFor(err);
        setError(key ? t(`errors.${key}`) : tCommon("error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Wordmark size="lg" href="/" />
        </div>

        <GlassCard padding="lg">
          <h1 className="font-display text-2xl font-semibold text-white">
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-white/55">
            {t("subtitle")}
          </p>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <Input
              label={t("emailLabel")}
              type="email"
              autoComplete="email"
              required
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="flex flex-col gap-1.5">
              <Input
                label={t("passwordLabel")}
                type="password"
                autoComplete="current-password"
                required
                placeholder={t("passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Link
                href="/forgot-password"
                className="self-end text-xs font-medium text-white/55 hover:text-flamingo-light"
              >
                {t("forgotPassword")}
              </Link>
            </div>

            {turnstile.enabled && turnstile.siteKey && (
              <TurnstileWidget
                key={turnstile.resetKey}
                siteKey={turnstile.siteKey}
                onToken={turnstile.setToken}
              />
            )}

            {error && (
              <p
                role="alert"
                className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger-light"
              >
                {error}
                {banned && (
                  <>
                    {" "}
                    <Link href="/appeal" className="font-medium underline hover:text-white">
                      {t("appeal")}
                    </Link>
                  </>
                )}
                {rejected && (
                  <>
                    {" "}
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="font-medium underline hover:text-white"
                    >
                      {t("contactSupport")}
                    </a>
                  </>
                )}
              </p>
            )}

            <Button type="submit" fullWidth size="lg" isLoading={submitting}>
              {t("submit")}
            </Button>
          </form>
        </GlassCard>

        <p className="mt-6 text-center text-sm text-white/50">
          {t("newHere")}{" "}
          <Link href="/join" className="font-medium text-flamingo hover:text-flamingo-light">
            {t("createAccount")}
          </Link>
        </p>
      </div>
    </div>
  );
}

/** Maps an API error to a `login.errors.*` key; null falls back to `common.error`. */
function errorKeyFor(err: unknown): string | null {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "INVALID_CREDENTIALS":
        return "invalidCredentials";
      case "BANNED":
        return "banned";
      case "REJECTED":
        return "rejected";
      case "RATE_LIMITED":
        return "rateLimited";
      case "VALIDATION":
        return "validation";
    }
  }
  return null;
}
